import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import PublicLayout from "@/components/layout/PublicLayout";
import PortalLayout from "@/components/layout/PortalLayout";
import ProtectedRoute from "@/components/ProtectedRoute";

// Public
import Home from "@/pages/public/Home";
import UsedCarLoans from "@/pages/public/UsedCarLoans";
import HowItWorks from "@/pages/public/HowItWorks";
import CarDrAdvantage from "@/pages/public/CarDrAdvantage";
import Dealers from "@/pages/public/Dealers";
import DSAPartners from "@/pages/public/DSAPartners";
import Contact from "@/pages/public/Contact";
import Privacy from "@/pages/public/Privacy";
import Terms from "@/pages/public/Terms";
import Apply from "@/pages/public/Apply";
import InviteLanding from "@/pages/public/InviteLanding";
import CheckEligibility from "@/pages/public/CheckEligibility";
import DocumentsRequired from "@/pages/public/DocumentsRequired";
import FAQs from "@/pages/public/FAQs";
import AboutRFC from "@/pages/public/AboutRFC";
import ConsentPolicy from "@/pages/public/ConsentPolicy";
import CountrySelect from "@/pages/public/CountrySelect";

// Auth
import Login from "@/pages/auth/Login";

// Borrower
import BorrowerDashboard from "@/pages/borrower/BorrowerDashboard";

// Dealer / DSA
import DealerDashboard from "@/pages/dealer/DealerDashboard";
import DSADashboard from "@/pages/dsa/DSADashboard";

// Admin / Underwriter / Collections
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ApplicationsList from "@/pages/admin/ApplicationsList";
import ApplicationDetail from "@/pages/admin/ApplicationDetail";
import UnderwriterDashboard from "@/pages/underwriter/UnderwriterDashboard";
import CollectionsDashboard from "@/pages/collections/CollectionsDashboard";
import SanctionLetter from "@/pages/SanctionLetter";
import LoanAgreement from "@/pages/LoanAgreement";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminCoLending from "@/pages/admin/AdminCoLending";
import AdminSellDown from "@/pages/admin/AdminSellDown";
import CreditPolicyEngine from "@/pages/admin/policy/CreditPolicyEngine";
import OperationsDashboard from "@/pages/operations/OperationsDashboard";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public site */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/used-car-loans" element={<UsedCarLoans />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/cardr-advantage" element={<CarDrAdvantage />} />
            <Route path="/dealer-partners" element={<Dealers />} />
            <Route path="/dsa-partners" element={<DSAPartners />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/check-eligibility" element={<CheckEligibility />} />
            <Route path="/documents-required" element={<DocumentsRequired />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/about-red-fort-capital" element={<AboutRFC />} />
            <Route path="/consent-policy" element={<ConsentPolicy />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/apply" element={<Apply />} />
          </Route>

          <Route path="/login" element={<Login />} />

          {/* Public invite landing (token-authed, no login) */}
          <Route path="/coborrower-invite/:token" element={<InviteLanding kindHint="coborrower" />} />
          <Route path="/guarantor-invite/:token" element={<InviteLanding kindHint="guarantor" />} />

          {/* Borrower */}
          <Route
            path="/borrower"
            element={
              <ProtectedRoute allow={["borrower"]}>
                <PortalLayout title="Borrower Portal" nav={[
                  { to: "/borrower", label: "My Applications", end: true },
                  { to: "/apply", label: "New Application" },
                ]} />
              </ProtectedRoute>
            }
          >
            <Route index element={<BorrowerDashboard />} />
            <Route path="applications/:id" element={<ApplicationDetail />} />
          </Route>

          {/* Dealer */}
          <Route
            path="/dealer"
            element={
              <ProtectedRoute allow={["dealer"]}>
                <PortalLayout title="Dealer Portal" nav={[
                  { to: "/dealer", label: "Dashboard", end: true },
                  { to: "/dealer/applications", label: "Applications" },
                  { to: "/dealer/new", label: "Create Lead" },
                ]} />
              </ProtectedRoute>
            }
          >
            <Route index element={<DealerDashboard />} />
            <Route path="applications" element={<ApplicationsList scope="dealer" />} />
            <Route path="applications/:id" element={<ApplicationDetail />} />
            <Route path="new" element={<Apply embedded source="dealer" />} />
          </Route>

          {/* DSA */}
          <Route
            path="/dsa"
            element={
              <ProtectedRoute allow={["dsa"]}>
                <PortalLayout title="DSA Portal" nav={[
                  { to: "/dsa", label: "Dashboard", end: true },
                  { to: "/dsa/applications", label: "Applications" },
                  { to: "/dsa/new", label: "Create Lead" },
                ]} />
              </ProtectedRoute>
            }
          >
            <Route index element={<DSADashboard />} />
            <Route path="applications" element={<ApplicationsList scope="dsa" />} />
            <Route path="applications/:id" element={<ApplicationDetail />} />
            <Route path="new" element={<Apply embedded source="dsa" />} />
          </Route>

          {/* Admin / Operations */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allow={["admin", "operations", "super_admin"]}>
                <PortalLayout title="Admin & Operations" nav={[
                  { to: "/admin", label: "Dashboard", end: true },
                  { to: "/admin/applications", label: "Applications" },
                  { to: "/admin/operations", label: "Operations" },
                  { to: "/admin/underwriter", label: "Underwriter View" },
                  { to: "/admin/collections", label: "Collections" },
                  { to: "/admin/policy", label: "Credit Policy Engine" },
                  { to: "/admin/users", label: "Users" },
                  { to: "/admin/colending", label: "Co-Lending" },
                  { to: "/admin/selldown", label: "Sell-Down" },
                ]} />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="applications" element={<ApplicationsList scope="admin" />} />
            <Route path="applications/:id" element={<ApplicationDetail />} />
            <Route path="operations" element={<OperationsDashboard />} />
            <Route path="underwriter" element={<UnderwriterDashboard />} />
            <Route path="collections" element={<CollectionsDashboard />} />
            <Route path="policy" element={<CreditPolicyEngine />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="colending" element={<AdminCoLending />} />
            <Route path="selldown" element={<AdminSellDown />} />
          </Route>

          {/* Underwriter */}
          <Route
            path="/underwriter"
            element={
              <ProtectedRoute allow={["underwriter"]}>
                <PortalLayout title="Underwriter Portal" nav={[
                  { to: "/underwriter", label: "Queue", end: true },
                  { to: "/underwriter/applications", label: "All Applications" },
                ]} />
              </ProtectedRoute>
            }
          >
            <Route index element={<UnderwriterDashboard />} />
            <Route path="applications" element={<ApplicationsList scope="underwriter" />} />
            <Route path="applications/:id" element={<ApplicationDetail />} />
          </Route>

          {/* Collections */}
          <Route
            path="/collections"
            element={
              <ProtectedRoute allow={["collections"]}>
                <PortalLayout title="Collections" nav={[
                  { to: "/collections", label: "Portfolio", end: true },
                ]} />
              </ProtectedRoute>
            }
          >
            <Route index element={<CollectionsDashboard />} />
            <Route path="applications/:id" element={<ApplicationDetail />} />
          </Route>

          {/* Sanction letter (print view) */}
          <Route path="/sanction/:id" element={
            <ProtectedRoute allow={["admin", "operations", "underwriter", "borrower", "dealer", "dsa"]}>
              <SanctionLetter />
            </ProtectedRoute>
          } />

          {/* Loan agreement + KFS (print view) */}
          <Route path="/agreement/:id" element={
            <ProtectedRoute allow={["admin", "operations", "underwriter", "borrower", "dealer", "dsa"]}>
              <LoanAgreement />
            </ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
